'use client';
import { Typography } from 'components';
import { RecentActivity } from 'gql/graphql';
import { RecentActivitiesNotFoundClipArt } from 'lib/svgs/ClipArts';
import _ from 'lodash';
import { useTranslations } from 'next-intl';
import { useRecentActivities } from 'providers/ActivitiesProviders/useRecentActivities';
import { getDayEnd } from 'utils/time/getDayEnd';
import BreakerCard from './BreakerCard';
import DashboardCards from './DashboardCards';
import { getTitleActivityFactory } from './DashboardCards/factories';

export enum KeysRecentActivities {
  TranslationRequestCreated = 'translation_request.create',
  TranslationRequestChanged = 'translation_request.status_change',
  CommentCreated = 'comment.create',
  ThreadCreated = 'universal_chat_thread.create',
  GeneratedContentCreate = 'generated_content.create'
}

type Activity = RecentActivity & {
  parameters: {
    from_status: string;
    to_status: string;
  };
};

export type RecentActivityExtended = Activity & {
  parameters: {
    from_status: string;
    to_status: string;
  };
  activities?: Activity[];
};

const groupSimilarActivitiesFactory = (activities: [string, RecentActivity[]][]) =>
  activities.map(([key, values]) => {
    const map = new Map();

    values.forEach((value) => {
      const title = getTitleActivityFactory(value as RecentActivityExtended)
        .split('(#')
        ?.at(0);
      const innerKey = `${title}-${value.parameters.from_status}-${value.parameters.to_status}}`;

      const existing = map.get(innerKey);
      return map.set(
        innerKey,
        existing
          ? {
              ...existing,
              activities: [...(existing.activities ?? [existing]), value]
            }
          : value
      );
    });

    return [key, Array.from(map.values())] as [string, RecentActivityExtended[]];
  });

const RecentActivities = () => {
  const t = useTranslations();

  const { data: dataRecentActivities } = useRecentActivities();

  const dataNonGroupedSimilars = Object.entries(
    _.groupBy(dataRecentActivities?.nodes ?? [], (activity) =>
      getDayEnd(activity.trackable?.updatedAt)
    )
  );

  const data = groupSimilarActivitiesFactory(dataNonGroupedSimilars);

  return (
    <div className="flex flex-1 flex-col rounded-md bg-white p-5 !pt-3 shadow-custom">
      <Typography variant="medium2" className="!m-0 text-tertiary-100 dark:text-white">
        {t('dashboard.recent_activities.title')}
      </Typography>
      <hr className="border-b-0.5 my-2 border-tertiary-600" />
      {!!dataRecentActivities?.nodes?.length ? (
        <div className="flex flex-col">
          {data?.map(([updatedAt, dataActivities]) => {
            const today = getDayEnd();
            const dateIso = getDayEnd(updatedAt);

            return (
              <div key={updatedAt}>
                {today !== dateIso && <BreakerCard date={updatedAt} />}
                <DashboardCards activities={dataActivities} />
              </div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center">
          <div className="my-6 flex flex-col items-center justify-center">
            <RecentActivitiesNotFoundClipArt className="scale-75" />
            <Typography variant="medium1" className="!mt-4 text-tertiary-100 dark:text-white">
              {t('dashboard.recent_activities.not_found.title')}
            </Typography>
            <Typography variant="light4" className="text-tertiary-200 dark:text-white">
              {t('dashboard.recent_activities.not_found.paragraph')}
            </Typography>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecentActivities;
